// Client-side profile-photo handling. Photos are decoded in the browser and
// re-encoded as a downscaled JPEG before upload, so even a 12 MP phone photo
// (or an HEIC/HEIF shot, which Safari can decode but Chrome cannot) arrives
// as a ~200-500 KB image/jpeg. The server's own caps (ProfilePhotoService)
// stay as the hard ceiling for misbehaving clients.
const DEFAULT_MAX_EDGE = 1600;
const DEFAULT_QUALITY = 0.88;

// Decodes the picked file into a drawable bitmap, honouring EXIF rotation.
// createImageBitmap is preferred (Safari can decode HEIC through it); where
// it is unavailable or fails, an <img> fallback is used - browsers apply EXIF
// orientation there by default.
async function decodeBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (e) {
      // Fall through to the <img> route - the format may not be bitmap-decodable here.
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('undecodable image')); };
    img.src = url;
  });
}

// Downscales (never upscales) and re-encodes an image file as a JPEG Blob-
// backed File named profile-photo.jpg (the name's extension is what the
// server's allowlist checks).
export async function downscalePhoto(file, { maxEdge = DEFAULT_MAX_EDGE, quality = DEFAULT_QUALITY } = {}) {
  const bitmap = await decodeBitmap(file);
  const width = bitmap.naturalWidth ?? bitmap.width;
  const height = bitmap.naturalHeight ?? bitmap.height;
  if (!width || !height) {
    if (bitmap.close) bitmap.close();
    throw new Error('undecodable image');
  }
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    if (bitmap.close) bitmap.close();
    throw new Error('canvas unavailable');
  }
  // Fill white first: JPEG has no alpha channel, and an empty canvas would
  // otherwise come out black behind transparent PNGs.
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);
  if (bitmap.close) bitmap.close();
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  if (!blob) throw new Error('image encoding failed');
  return new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
}
